import { useEffect, useRef, useState } from "react";
import { X, ArrowLeft, ArrowRight, Sparkles, Loader2, MessageCircle, ChevronDown, RotateCcw, Wand2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TextArea, PrimaryButton, SecondaryButton, Overline } from "@/components/ui-kit";

/**
 * Step-Zero Interview Wizard.
 * Pulls predefined questions from /api/ai/interview/questions, walks the user
 * through them one at a time, then POSTs to /api/ai/generate-plan which returns
 * a fully-populated project. Calls onCreated(project) when done.
 */
const STEPS = [
  "interview.step1",
  "interview.step2",
  "interview.step3",
  "interview.step4",
  "interview.step5",
  "interview.step6",
];

export default function InterviewWizard({ onClose, onCreated }) {
  const { t, lang } = useLang();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);

  // Coach chat state — one chat per question key, loaded once from backend.
  // chats: { question_key: [{role, content, at}] }
  const [chats, setChats] = useState({});
  const [chatOpenFor, setChatOpenFor] = useState(null); // question_key currently expanded
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [refining, setRefining] = useState(false);
  const [greetingFor, setGreetingFor] = useState(null); // question_key currently being greeted
  // Track which questions we've already tried to auto-greet in this modal
  // session so we don't spam the endpoint if the user bounces back and forth.
  const greetedRef = useRef(new Set());
  // Track which questions the user has explicitly closed so we don't auto-
  // reopen the panel on subsequent visits during the same session.
  const closedByUserRef = useRef(new Set());
  const chatEndRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [qRes, sRes] = await Promise.all([
          api.get(`/ai/interview/questions?language=${lang}`),
          api.get(`/ai/interview/session?language=${lang}`),
        ]);
        setQuestions(qRes.data.questions);
        // Prefill answers from stored draft_answer per question
        const storedChats = sRes.data.chats || {};
        const initialAnswers = {};
        const initialChats = {};
        Object.entries(storedChats).forEach(([k, v]) => {
          if (v?.draft_answer) initialAnswers[k] = v.draft_answer;
          initialChats[k] = v?.messages || [];
        });
        if (Object.keys(initialAnswers).length) setAnswers(initialAnswers);
        setChats(initialChats);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  // Cycle through pseudo "thinking" steps during generation
  useEffect(() => {
    if (!generating) return;
    const id = setInterval(() => setGenStep((s) => (s + 1) % STEPS.length), 4500);
    return () => clearInterval(id);
  }, [generating]);

  const q = questions[idx];
  const totalQ = questions.length;
  const isLast = idx === totalQ - 1;
  const currentAnswer = q ? answers[q.key] || "" : "";

  // Debounced auto-save of the current textarea value to backend, so the
  // coach always has an up-to-date picture of what the user has drafted for
  // this question (even if they never used "Use as answer"). Prevents the
  // AI from starting from zero when they switch questions.
  const saveTimer = useRef(null);
  function scheduleSaveAnswer(qKey, value) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Fire-and-forget — auto-save should never toast on failure.
      api.put("/ai/interview/answer", {
        question_key: qKey,
        answer: value,
        language: lang,
      }).catch(() => { /* silent */ });
    }, 800);
  }
  function flushSaveAnswer(qKey, value) {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    return api.put("/ai/interview/answer", {
      question_key: qKey,
      answer: value,
      language: lang,
    }).catch(() => { /* silent */ });
  }

  function setAnswer(v) {
    if (!q) return;
    setAnswers((p) => ({ ...p, [q.key]: v }));
    scheduleSaveAnswer(q.key, v);
  }

  function back() {
    if (q) flushSaveAnswer(q.key, currentAnswer);
    setIdx((i) => Math.max(0, i - 1));
  }
  function next() {
    if (q) flushSaveAnswer(q.key, currentAnswer);
    setIdx((i) => Math.min(totalQ - 1, i + 1));
  }

  async function finish() {
    // Flush any pending textarea autosave so backend has final values.
    if (q) {
      try { await flushSaveAnswer(q.key, currentAnswer); } catch { /* ignore */ }
    }
    const payload = {
      mode: "interview",
      language: lang,
      interview: questions.map((qq) => ({ key: qq.key, answer: (answers[qq.key] || "").trim() })),
    };
    setGenerating(true);
    try {
      const startRes = await api.post("/ai/generate-plan", payload);
      const jobId = startRes.data.job_id;
      // Poll every 4 seconds, up to 4 minutes total
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const poll = await api.get(`/ai/generate-plan/jobs/${jobId}`);
        if (poll.data.status === "done" && poll.data.project) {
          onCreated(poll.data.project);
          return;
        }
        if (poll.data.status === "failed") {
          throw new Error(poll.data.error || "Generation failed");
        }
      }
      throw new Error(lang === "es" ? "Tiempo de espera agotado" : "Generation timeout");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setGenerating(false);
    }
  }

  // ─── Coach chat handlers ────────────────────────────────────────────────
  const currentChat = q ? (chats[q.key] || []) : [];

  // PROACTIVE GREETING: when the user lands on a question we do two things:
  //   1. If the chat is EMPTY and there's context from other questions to
  //      draw on, ask the backend to seed a short opener.
  //   2. Either way, if the last message in the chat comes from the coach
  //      (i.e., there's an unread message for the user), auto-expand the
  //      panel so they see it. The user can still close it manually.
  useEffect(() => {
    if (!q || loading) return;
    const key = q.key;
    if (greetedRef.current.has(key)) return;
    if (closedByUserRef.current.has(key)) {
      greetedRef.current.add(key);
      return;
    }

    const existing = chats[key] || [];
    const lastMsg = existing[existing.length - 1];

    // Case (a): chat already has messages and the latest is from the coach
    // → surface it by opening the panel.
    if (existing.length > 0) {
      greetedRef.current.add(key);
      if (lastMsg?.role === "assistant") {
        setChatOpenFor(key);
      }
      return;
    }

    // Case (b): empty chat — attempt a proactive greeting if there's
    // context from other questions/answers. Otherwise silently skip.
    const hasContext =
      Object.entries(answers).some(([k, v]) => k !== key && (v || "").trim().length > 0) ||
      Object.entries(chats).some(([k, msgs]) => k !== key && (msgs || []).length > 0);
    if (!hasContext) {
      greetedRef.current.add(key);
      return;
    }

    let cancelled = false;
    greetedRef.current.add(key);
    setGreetingFor(key);
    (async () => {
      try {
        const res = await api.post("/ai/interview/coach/greet", {
          question_key: key,
          language: lang,
        });
        if (cancelled) return;
        if (res.data.greeted && (res.data.messages || []).length > 0) {
          setChats((c) => ({ ...c, [key]: res.data.messages }));
          setChatOpenFor(key); // auto-expand so the user sees the message
        }
      } catch {
        /* silent — proactive is a nice-to-have, never blocks */
      } finally {
        if (!cancelled) setGreetingFor(null);
      }
    })();
    return () => { cancelled = true; };
  }, [q?.key, loading]);

  useEffect(() => {
    // Auto-scroll chat to bottom when new messages arrive
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [currentChat.length, chatOpenFor]);

  async function sendCoachMessage() {
    if (!q) return;
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatBusy(true);
    // Optimistic append of the user turn
    setChats((c) => ({
      ...c,
      [q.key]: [...(c[q.key] || []), { role: "user", content: text, at: new Date().toISOString() }],
    }));
    setChatInput("");
    try {
      const res = await api.post("/ai/interview/coach", {
        question_key: q.key,
        message: text,
        language: lang,
        current_answer: currentAnswer,
      });
      // Replace local chat with authoritative server list (includes assistant reply)
      setChats((c) => ({ ...c, [q.key]: res.data.messages }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      // Roll back optimistic message on error so the user can retry
      setChats((c) => ({
        ...c,
        [q.key]: (c[q.key] || []).slice(0, -1),
      }));
    } finally {
      setChatBusy(false);
    }
  }

  async function refineToAnswer() {
    if (!q) return;
    setRefining(true);
    try {
      const res = await api.post("/ai/interview/refine", {
        question_key: q.key,
        language: lang,
        current_answer: currentAnswer,
      });
      setAnswers((p) => ({ ...p, [q.key]: res.data.answer || "" }));
      toast.success(lang === "es" ? "Respuesta redactada por el asesor" : "Answer drafted by advisor");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefining(false);
    }
  }

  async function resetCoachChat() {
    if (!q) return;
    if (!window.confirm(
      lang === "es"
        ? "¿Borrar toda la conversación con el asesor para esta pregunta?"
        : "Clear all coach chat for this question?"
    )) return;
    try {
      await api.post("/ai/interview/reset", { question_key: q.key });
      setChats((c) => ({ ...c, [q.key]: [] }));
      toast.success(lang === "es" ? "Chat borrado" : "Chat cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const coachT = lang === "es"
    ? {
        openBtn: "Hablar con un asesor",
        subtitle: "Chatea con la IA para pulir tu respuesta paso a paso",
        placeholder: "Cuéntale más al asesor…",
        send: "Enviar",
        thinking: "El asesor está pensando…",
        useAsAnswer: "Usar como respuesta",
        refining: "Redactando…",
        close: "Cerrar chat",
        reset: "Empezar de nuevo",
        emptyHint: "El asesor te hará preguntas para ayudarte a pensar. Cuando tengas suficiente, pulsa \"Usar como respuesta\" y él redactará la versión final.",
      }
    : {
        openBtn: "Talk to an advisor",
        subtitle: "Chat with AI to polish your answer step by step",
        placeholder: "Tell the advisor more…",
        send: "Send",
        thinking: "Advisor is thinking…",
        useAsAnswer: "Use as answer",
        refining: "Drafting…",
        close: "Close chat",
        reset: "Start over",
        emptyHint: "The advisor will ask you questions to help you think. When you have enough, hit \"Use as answer\" and they'll draft the final version.",
      };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="interview-wizard-overlay"
    >
      <div
        className="bg-[#F9F8F6] w-full max-w-3xl rounded-sm shadow-2xl flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-5 border-b border-[#E8E6E1] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-[#B89154]" strokeWidth={1.5} />
            <div>
              <Overline>
                {t("interview.title")}
              </Overline>
              {!generating && totalQ > 0 && (
                <div
                  className="mt-1 text-xs text-[#8A8886] font-mono"
                  style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                  data-testid="interview-progress"
                >
                  {t("interview.progress")} {String(idx + 1).padStart(2, "0")} {t("interview.of")} {String(totalQ).padStart(2, "0")}
                </div>
              )}
            </div>
          </div>
          {!generating && (
            <button
              onClick={onClose}
              className="text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
              data-testid="interview-close-btn"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {loading && (
            <div className="text-center text-[#5C5B5A]">{t("common.loading")}</div>
          )}

          {generating && (
            <div className="text-center py-12 space-y-6" data-testid="interview-generating">
              <Loader2 className="h-10 w-10 text-[#B89154] mx-auto animate-spin" strokeWidth={1.2} />
              <h2
                className="text-3xl tracking-tight text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              >
                {t("interview.generating_title")}
              </h2>
              <p className="text-sm text-[#5C5B5A] max-w-md mx-auto">
                {t("interview.generating_subtitle")}
              </p>
              <div className="border-t border-[#E8E6E1] pt-6 max-w-md mx-auto space-y-1.5">
                {STEPS.map((stepKey, i) => (
                  <div
                    key={stepKey}
                    className={`flex items-center gap-3 text-sm transition-all ${
                      i < genStep ? "text-[#2563EB]" : i === genStep ? "text-[#1A1A1A] font-medium" : "text-[#8A8886]"
                    }`}
                  >
                    <span className="font-mono text-xs w-6" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{i < genStep ? "✓" : i === genStep ? "›" : "·"}</span>
                    <span>{t(stepKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && !generating && q && (
            <div className="space-y-6">
              {/* Progress bar */}
              <div className="h-[2px] bg-[#E8E6E1] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#2563EB] transition-all duration-500"
                  style={{ width: `${((idx + 1) / totalQ) * 100}%` }}
                />
              </div>

              <h2
                className="text-3xl tracking-tight text-[#1A1A1A] leading-tight"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                data-testid="interview-question-text"
              >
                {q.text}
              </h2>

              <TextArea
                value={currentAnswer}
                onChange={setAnswer}
                placeholder={q.placeholder}
                rows={6}
                testId={`interview-answer-${q.key}`}
              />

              {/* Coach chat trigger + panel */}
              {chatOpenFor !== q.key ? (
                <button
                  type="button"
                  onClick={() => setChatOpenFor(q.key)}
                  className="group inline-flex items-center gap-2 text-sm text-[#2563EB] hover:text-[#7C3AED] transition-colors"
                  data-testid={`coach-open-${q.key}`}
                >
                  {greetingFor === q.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  <span className="font-medium">
                    {greetingFor === q.key
                      ? (lang === "es" ? "El asesor te está preparando algo…" : "Advisor is preparing something for you…")
                      : currentChat.length > 0
                      ? (lang === "es" ? `Continuar con el asesor (${Math.ceil(currentChat.length / 2)})` : `Continue with advisor (${Math.ceil(currentChat.length / 2)})`)
                      : coachT.openBtn}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-60 group-hover:translate-y-0.5 transition-transform" strokeWidth={1.5} />
                </button>
              ) : (
                <div
                  className="border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/[0.03] via-white to-[#7C3AED]/[0.03] rounded-sm overflow-hidden"
                  data-testid={`coach-panel-${q.key}`}
                >
                  {/* Header */}
                  <div className="px-5 py-3 border-b border-[#E8E6E1] flex items-center justify-between bg-white/60">
                    <div className="flex items-center gap-2.5">
                      <div className="inline-flex items-center justify-center h-7 w-7 rounded-sm bg-gradient-to-br from-[#2563EB] to-[#7C3AED] text-white">
                        <MessageCircle className="h-3.5 w-3.5" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-[#7C3AED] font-semibold">
                          {lang === "es" ? "Asesor IA" : "AI Advisor"}
                        </div>
                        <div className="text-[11px] text-[#8A8886]">{coachT.subtitle}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {currentChat.length > 0 && (
                        <button
                          onClick={resetCoachChat}
                          title={coachT.reset}
                          className="p-1.5 text-[#8A8886] hover:text-[#9E2A2B] transition-colors"
                          data-testid={`coach-reset-${q.key}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (q) closedByUserRef.current.add(q.key);
                          setChatOpenFor(null);
                        }}
                        title={coachT.close}
                        className="p-1.5 text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
                        data-testid={`coach-close-${q.key}`}
                      >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="max-h-72 overflow-y-auto px-5 py-4 space-y-3">
                    {currentChat.length === 0 && (
                      <div className="text-xs text-[#8A8886] italic leading-relaxed border border-dashed border-[#E8E6E1] rounded-sm p-3 bg-white/60">
                        {coachT.emptyHint}
                      </div>
                    )}
                    {currentChat.map((m, i) => (
                      <div
                        key={i}
                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap rounded-sm px-3.5 py-2.5 ${
                            m.role === "user"
                              ? "bg-[#1A1A1A] text-white"
                              : "bg-white border border-[#E8E6E1] text-[#1A1A1A]"
                          }`}
                          data-testid={`coach-msg-${m.role}-${i}`}
                        >
                          {m.content}
                        </div>
                      </div>
                    ))}
                    {chatBusy && (
                      <div className="flex justify-start">
                        <div className="inline-flex items-center gap-2 bg-white border border-[#E8E6E1] rounded-sm px-3.5 py-2.5 text-sm text-[#8A8886]">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                          {coachT.thinking}
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Input + actions */}
                  <div className="px-5 py-3 border-t border-[#E8E6E1] bg-white/60 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey && !chatBusy) {
                            e.preventDefault();
                            sendCoachMessage();
                          }
                        }}
                        placeholder={coachT.placeholder}
                        className="flex-1 min-w-0 border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-sm px-3 py-2 outline-none"
                        disabled={chatBusy}
                        data-testid={`coach-input-${q.key}`}
                      />
                      <button
                        onClick={sendCoachMessage}
                        disabled={chatBusy || !chatInput.trim()}
                        className="inline-flex items-center gap-1.5 bg-[#1A1A1A] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#000] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid={`coach-send-${q.key}`}
                      >
                        {chatBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {coachT.send}
                      </button>
                    </div>
                    {currentChat.length >= 2 && (
                      <button
                        onClick={refineToAnswer}
                        disabled={refining}
                        className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-4 py-2 rounded-sm hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                        data-testid={`coach-refine-${q.key}`}
                      >
                        {refining ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                            {coachT.refining}
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            {coachT.useAsAnswer} ↑
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !generating && q && (
          <div className="px-8 py-5 border-t border-[#E8E6E1] flex items-center justify-between">
            <SecondaryButton onClick={back} disabled={idx === 0} testId="interview-back-btn">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              {t("interview.back")}
            </SecondaryButton>
            <div className="flex items-center gap-3">
              {!isLast && (
                <button
                  onClick={next}
                  className="text-sm text-[#8A8886] hover:text-[#1A1A1A] transition-colors px-3 py-2"
                  data-testid="interview-skip-btn"
                >
                  {t("interview.skip")}
                </button>
              )}
              {isLast ? (
                <PrimaryButton onClick={finish} testId="interview-finish-btn">
                  <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                  {t("interview.finish")}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={next} testId="interview-next-btn">
                  {t("interview.next")}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </PrimaryButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
